import React, { useState, useEffect, useRef } from "react";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import {
  getAppointments,
  getRecentEMRAppointmentLoadDetails,
  uploadEMRAppointmentLoadFile,
} from "apiHandler/actions";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import { IconButton, CircularProgress, Box } from "@mui/material";
import GetAppIcon from "@material-ui/icons/GetApp";
import { format } from "date-fns";
import WeekPicker from "components/UI/weekPicker";
import {
  displayDateFormatter,
  isoDateFormatter,
  addMinutes,
  BackButton,
} from "components/helpers";
import { useUserData } from "components/customHooks/userDataContext";
import AddAppointment from "components/modals/AddAppointment";
import AddPatient from "components/modals/AddPatient";
import useSnackbar from "components/customHooks/useSnackbar";
import { useLocation, useNavigate } from "react-router-dom";
import UploadDictationModal from "components/modals/UploadDictation";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers";
import { DATE_FORMAT, trimmedDate } from "utils/helper";
import EMRAppointmentLoadDetailsDialog from "./modals/EMRAppointmentLoadDetailsDialog";
import { useDispatch, useSelector } from "react-redux";
import AlertBox from "./modals/EMRAppointmentLoadDetailsDialog/alertBox";
import { setLoadDetails } from "../redux/reducers/appointment";

const ConditionalDatePicker = ({ date, setDate, currentViewName }) => {
  switch (currentViewName) {
    case "Week":
      return <WeekPicker date={date} setDate={setDate} />;
    case "Month":
      return (
        <DatePicker
          value={dayjs(date)}
          onChange={(e) => setDate(e)}
          views={["year", "month"]}
          minDate={dayjs("2019-01-01")}
          maxDate={dayjs()}
          slotProps={{ textField: { variant: "standard" } }}
          format={DATE_FORMAT}
        />
      );
    default:
      return (
        <DatePicker
          value={dayjs(date)}
          onChange={(e) => setDate(e)}
          minDate={dayjs("2019-01-01")}
          maxDate={dayjs()}
          slotProps={{ textField: { variant: "standard" } }}
          format={DATE_FORMAT}
        />
      );
  }
};

const CalendarHeader = ({
  currentViewName,
  setCurrentViewName,
  setLoading,
  setAppointments,
  date,
  setDate,
  onPrint,
  data,
}) => {
  const [showAddAppntModal, setShowAddAppntModal] = useState(false);
  const [showAddPatientModal, setShowAddPatientModal] = useState(false);
  const [uploadDictn, setUploadDict] = useState(false);
  const [loadingPrint, setLoadingPrint] = useState(false);
  const [uploadingAppointments, setUploadingAppointments] = useState(false);
  const [openLoadDetailsDialog, setOpenLoadDetailsDialog] = useState(false);
  const [loadingLoadDetails, setLoadingLoadDetails] = useState(false);
  const loadDetails = useSelector((state) => state.appointment.loadDetails);
  const dispatch = useDispatch();

  const appointmentFileInputRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const userData = useUserData();
  const isProvider = userData?.user_details?.user_role === "Provider";
  const token = userData?.access_token;
  const { CustomSnackbar, setSnackbarMessage } = useSnackbar();

  const fetchData = async (useLocationState = true) => {
    setLoading(true);
    const payload = {
      search_criteria: {
        practice_id: `${userData.selected_practice}`,
        user_id: userData?.email,
        ...(isProvider && { npi: userData?.user_details?.npi }),
      },
    };

    let startDateTime;
    let endDateTime;

    if (currentViewName === "Month") {
      startDateTime = dayjs(date).startOf("month").format("MM/DD/YYYY");
      endDateTime = dayjs(date)
        .add(1, "month")
        .startOf("month")
        .format("MM/DD/YYYY");
    } else if (currentViewName === "Week") {
      startDateTime = dayjs(date).startOf("week").format("MM/DD/YYYY");
      endDateTime = dayjs(startDateTime).add(7, "days").format("MM/DD/YYYY");
    }

    if (location?.state && useLocationState) {
      const { dos, location: appt_location, reminder_status } = location.state;
      if (dos) payload.search_criteria.dos = displayDateFormatter(dos);
      if (appt_location) payload.search_criteria.location = appt_location;
      if (reminder_status) {
        payload.search_criteria.reminder_status = reminder_status;
      }
    } else {
      if (startDateTime && endDateTime) {
        payload.search_criteria.start_dos = startDateTime;
        payload.search_criteria.end_dos = endDateTime;
      } else {
        payload.search_criteria.dos = format(date, "MM/dd/yyyy");
      }
    }

    const response = await getAppointments(payload, token);

    if (response) {
      setAppointments(
        response
          .map((a) => ({
            ...a,
            patient_name: `${a.first_name || ""} ${a.last_name || ""}`.trim(),
            title: `${a.first_name || ""} ${a.last_name || ""}`.trim(),
            startDate: `${isoDateFormatter(a.dos)}T${
              a.dos_time && addMinutes(a.dos_time, 0)
            }`,
            endDate: `${isoDateFormatter(a.dos)}T${
              a.dos_time && addMinutes(a.dos_time, parseFloat(a.duration))
            }`,
            appntId: a.id,
          }))
          .sort((item1, item2) => {
            if (item1.startDate < item2.startDate) return -1;
            if (item1.startDate > item2.startDate) return 1;
            return 0;
          }),
      );
    } else {
      setAppointments([]);
      setSnackbarMessage("Failed To Fetch Appointments");
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [date, currentViewName]);

  const getEMRAppointmentsLoadDetails = async () => {
    try {
      setLoadingLoadDetails(true);
      setOpenLoadDetailsDialog(true);

      const payload = {
        practice_id: `${userData.selected_practice}`,
      };

      const res = await getRecentEMRAppointmentLoadDetails(payload, token);

      if (Array.isArray(res)) {
        dispatch(setLoadDetails(res));
      } else {
        dispatch(setLoadDetails([]));
        setSnackbarMessage("No load details found");
      }
    } catch (error) {
      console.log("error: ", error);
      dispatch(setLoadDetails([]));
      setSnackbarMessage(
        error?.response?.data?.message || "Failed to fetch load details",
      );
    } finally {
      setLoadingLoadDetails(false);
    }
  };

  const changeDate = (type) => {
    let newDate = dayjs(date);

    if (currentViewName === "Month") {
      newDate =
        type === "increment"
          ? newDate.add(1, "month")
          : newDate.subtract(1, "month");
    } else {
      const numberOfDaysToAdd = currentViewName === "Week" ? 7 : 1;
      newDate =
        type === "increment"
          ? newDate.add(numberOfDaysToAdd, "day")
          : newDate.subtract(numberOfDaysToAdd, "day");
    }

    setDate(newDate.toDate());
  };

  const onUploadAppointment = async (file) => {
    if (!file) return;

    try {
      setUploadingAppointments(true);
      setSnackbarMessage("Uploading appointments...");

      const formData = new FormData();
      formData.append("file", file);
      formData.append(
        "request_json",
        JSON.stringify({
          practice_id: `${userData?.selected_practice}`,
        }),
      );

      const res = await uploadEMRAppointmentLoadFile(formData, token);

      if (res?.status === "Success") {
        setSnackbarMessage("Appointments uploaded successfully");
        fetchData(false);
      } else {
        setSnackbarMessage(res?.message || "Failed to upload appointments");
      }
    } catch (error) {
      console.log("error: ", error);
      setSnackbarMessage(
        error?.response?.data?.message || "Error uploading appointments",
      );
    } finally {
      setUploadingAppointments(false);
      if (appointmentFileInputRef.current) {
        appointmentFileInputRef.current.value = "";
      }
    }
  };

  const handleAppointmentFileChange = async (event) => {
    const file = event.target.files?.[0];
    await onUploadAppointment(file);
  };

  const columns = [
    { field: "id", headerName: "ID" },
    { field: "patient_id", headerName: "Patient ID" },
    {
      field: "patient_name",
      headerName: "Patient Name",
      formatter: (row) =>
        `${row.first_name || ""} ${row.last_name || ""}`.trim(),
    },
    {
      field: "dob",
      headerName: "DOB",
      formatter: (value) => trimmedDate(value),
    },
    {
      field: "dos",
      headerName: "Date of Service",
      formatter: (value) => trimmedDate(value),
    },
    {
      field: "dos_time",
      headerName: "Time",
    },
    { field: "appt_type", headerName: "Appointment Type" },
    { field: "confirm_status", headerName: "Status" },
    { field: "reminder_status", headerName: "Reminder Status" },
  ];

  const downloadCSV = () => {
    const headers = columns.map((col) => col.headerName);
    const rows = data.map((item) =>
      columns.map((col) => {
        if (col.formatter) {
          if (col.field === "patient_name") {
            return col.formatter(item);
          }
          return col.formatter(item[col.field]);
        }
        return item[col.field] || "";
      }),
    );

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((val) => `"${val}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "filtered_data.csv";
    link.click();
    URL.revokeObjectURL(link.href);
  };

  if (location?.state) {
    return (
      <>
        <div className="calendar-header">
          <BackButton onClick={() => navigate(-1)} />
          <button
            onClick={() => {
              navigate("", {
                replace: true,
                state: null,
              });
              fetchData(false);
            }}
            className="blank-hover-btn"
          >
            Clear Filter
          </button>
        </div>
      </>
    );
  }

  return (
    <div className="calendar-header">
      <CustomSnackbar />
      <Box sx={{ width: "100%", mb: 2 }}>
        <AlertBox rows={loadDetails} />
      </Box>
      <input
        ref={appointmentFileInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        style={{ display: "none" }}
        onChange={handleAppointmentFileChange}
      />

      <div className="calendar">
        <button
          className={`time-btn ${
            currentViewName === "Day" ? "selected-btn" : null
          }`}
          onClick={() => setCurrentViewName("Day")}
        >
          Day
        </button>
        <button
          className={`time-btn ${
            currentViewName === "Week" ? "selected-btn" : null
          }`}
          onClick={() => setCurrentViewName("Week")}
        >
          Week
        </button>
        <button
          className={`time-btn ${
            currentViewName === "Month" ? "selected-btn" : null
          }`}
          onClick={() => setCurrentViewName("Month")}
        >
          Month
        </button>
      </div>

      <div
        className="flex-align-center"
        style={{ marginLeft: "16px", marginRight: "16px" }}
      >
        <button
          className="blank-hover-btn"
          onClick={getEMRAppointmentsLoadDetails}
        >
          Load Details
        </button>

        <button
          className="blank-hover-btn"
          onClick={() => appointmentFileInputRef.current?.click()}
          disabled={uploadingAppointments}
        >
          {uploadingAppointments
            ? "Uploading Appointments..."
            : "Upload Appointments"}
        </button>

        <button className="blank-hover-btn" onClick={() => setUploadDict(true)}>
          Upload Dictation
        </button>

        {currentViewName === "Day" ? (
          <button
            onClick={async () => {
              if (loadingPrint) return;
              setLoadingPrint(true);
              await onPrint();
              setLoadingPrint(false);
            }}
            className="blank-hover-btn"
            style={{ minWidth: 136 }}
          >
            {loadingPrint ? (
              <CircularProgress color="secondary" size={15} />
            ) : (
              "Print Encounter Forms"
            )}
          </button>
        ) : null}

        <button
          onClick={() => setShowAddAppntModal(true)}
          className="blank-hover-btn"
        >
          Add Appointment
        </button>

        <button
          onClick={() => setShowAddPatientModal(true)}
          className="blank-hover-btn"
        >
          Add Patient
        </button>

        <IconButton style={{ marginRight: 2 }} onClick={downloadCSV}>
          <GetAppIcon />
        </IconButton>

        <div>
          <button
            style={{
              background: "transparent",
              border: "1px solid rgba(0, 0, 0, 0.23)",
              borderRadius: "4px",
              padding: "5px 15px",
            }}
            onClick={() => setDate(new Date())}
          >
            Today
          </button>

          <IconButton onClick={() => changeDate("decrement")}>
            <ChevronLeftIcon />
          </IconButton>

          <IconButton onClick={() => changeDate("increment")}>
            <ChevronRightIcon />
          </IconButton>

          <ConditionalDatePicker
            date={date}
            setDate={setDate}
            currentViewName={currentViewName}
          />
        </div>
      </div>

      {showAddPatientModal ? (
        <AddPatient
          onClose={() => setShowAddPatientModal(false)}
          setSnackbarMessage={setSnackbarMessage}
        />
      ) : null}

      {showAddAppntModal && (
        <AddAppointment
          onClose={(isAdded) => {
            if (isAdded) fetchData();
            setShowAddAppntModal(false);
          }}
        />
      )}

      {uploadDictn && (
        <UploadDictationModal
          onClose={() => setUploadDict(false)}
          setSnackbarMessage={setSnackbarMessage}
          fetchData={fetchData}
        />
      )}
      <EMRAppointmentLoadDetailsDialog
        open={openLoadDetailsDialog}
        onClose={() => setOpenLoadDetailsDialog(false)}
        rows={loadDetails}
        loading={loadingLoadDetails}
        onRefresh={getEMRAppointmentsLoadDetails}
      />
    </div>
  );
};

export default CalendarHeader;
